import { Formik } from "formik";
import { useState, useEffect } from "react";
import CMSFormInput from "../formFields/CMSFormInput";
import GeneralButton from "../common/GeneralButton";
import ContainerLoading from "../common/loading/ContainerLoading";
import { motion } from "framer-motion";
import success from "../../assets/images/success.png";
import { useToast } from "../contexts/ToastContext";
import { useNavigate } from "react-router-dom";
import {
  emailSchema,
  forgotPassEmailSchema,
  newPasswordSchema,
  otpSchema,
} from "../utils/validations";
import axios from "../../config/axios";
import { TiChevronLeft } from "react-icons/ti";

const ForgotPassword = () => {
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState("idle");
  const [customerId, setCustomerId] = useState("");
  const [email, setEmail] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(60);
  const { addToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    let interval;
    if (resendDisabled) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setResendDisabled(false);
            return 60;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendDisabled]);

  const handleEmailSubmit = async (values) => {
    setStatus("loading");
    try {
      const { data } = await axios.post(
        "/dashboard/customers/forgot-password",
        {
          email: values.email,
        }
      );
      setEmail(values.email);
      if (data?.customerId) {
        setCustomerId(data.customerId);
      }
      addToast(data.message || "OTP sent to your email", "success");
      setStep(2);
      // start timer after sending OTP
      setResendDisabled(true);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to send OTP", "error");
      setTimeout(() => {
        setError("");
      }, 5000);
    } finally {
      setStatus("idle");
    }
  };

  const handleResendOtp = async (values) => {
    if (!email) return;
    setStatus("loading");
    try {
      await axios.post("/dashboard/customers/resend-pin", values);
      addToast("New OTP sent to your email", "success");
      setResendDisabled(true);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Failed to resend OTP", "error");
      setTimeout(() => {
        setError("");
      }, 5000);
    } finally {
      setStatus("idle");
    }
  };

  const handleOtpSubmit = async (values) => {
    setStatus("loading");
    try {
      const { data } = await axios.post(
        "/dashboard/customers/pin-verification",
        values
      );
      addToast("OTP verified successfully", "success");
      setOtpToken(data.token);
      setStep(3);
      setStatus("idle");
    } catch {
      setStatus("idle");
      setError("Invalid OTP, please try again", "error");
      setTimeout(() => {
        setError("");
      }, 5000);
    }
  };

  const handlePasswordSubmit = async (values) => {
    setStatus("loading");
    try {
      const { data } = await axios.post("/dashboard/customers/reset-password", {
        token: otpToken,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      addToast(data.message || "Password changed successfully", "success");
      setStatus("success");
      setTimeout(() => navigate("/signin"), 10000);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message || "Failed to change password, try again.",
        "error"
      );
      setTimeout(() => {
        setError("");
      }, 5000);
      setStatus("idle");
    }
  };

  return (
    <>
      <p className="text-34pt mb-4 lg:mb-6">Forgot Password</p>

      <div
        className={` rounded-[15px] relative ${
          status === "success"
            ? "lg:max-w-[85%] 2.5xl:max-w-[75%] bg-secondary p-10 2.5xl:p-20"
            : "lg:max-w-[60%] bg-[#DEE2DD]/50  p-10 lg:p-20"
        } `}
      >
        {status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center rounded-[15px] z-10">
            <ContainerLoading />
          </div>
        )}

        {status === "success" ? (
          <motion.div
            initial={{ x: -200, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -200, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <div className="grid grid-cols-1 gap-8 items-start">
              <div className="w-full rounded-[15px] p-0 md:px-10 md:py-10 ">
                <div className="grid grid-cols-1 lg:grid-cols-[2.5fr_1fr] gap-2 lg:gap-10">
                  <div className="md:p-5 flex flex-col justify-between order-2 lg:order-1">
                    <p className="text-white text-28pt md:text-48pt leading-none mb-4 md:mb-8 text-center lg:text-left">
                      Password Reset Successful!
                    </p>
                    <p className="text-orange text-[25px] md:text-[34px] leading-none text-center lg:text-left">
                      You can now log in with your new password.
                    </p>
                  </div>
                  <div className="flex items-center justify-center order-1 lg:order-2 mb-4 md:mb-0">
                    <img
                      src={success}
                      alt="success"
                      className="w-[80px] h-[80px] md:w-[110px] md:h-[110px] lg:w-[134px] lg:h-[134px]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <>
            {step === 1 && (
              <Formik
                initialValues={{ email: "" }}
                validationSchema={forgotPassEmailSchema}
                onSubmit={handleEmailSubmit}
              >
                {(form) => (
                  <form
                    onSubmit={form.handleSubmit}
                    className="grid grid-cols-1 gap-6"
                  >
                    <CMSFormInput
                      name="email"
                      label="Email"
                      className="1"
                      form={form}
                      required
                    />
                    {error && (
                      <div
                        className={`relative transition-all duration-300 ease-in-out ${
                          error
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 -translate-y-2"
                        }`}
                      >
                        {error && <span className="errorMessage">{error}</span>}
                      </div>
                    )}
                    <div className="flex justify-end">
                      <GeneralButton
                        text="Submit"
                        title="Click to get verification pin"
                        type="submit"
                        className="text-24pt px-4"
                      />
                    </div>
                  </form>
                )}
              </Formik>
            )}

            {step === 2 && (
              <Formik
                initialValues={{
                  customerId: customerId || "",
                  type: "Forgot Password",
                  pin: "",
                }}
                validationSchema={otpSchema}
                onSubmit={handleOtpSubmit}
              >
                {(form) => (
                  <form
                    onSubmit={form.handleSubmit}
                    className="grid grid-cols-1 gap-8 md:gap-6"
                  >
                    <CMSFormInput
                      name="pin"
                      label="Enter OTP"
                      className="1"
                      form={form}
                      required
                    />
                    {error && (
                      <div
                        className={`relative transition-all duration-300 ease-in-out ${
                          error
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 -translate-y-2"
                        }`}
                      >
                        {error && <span className="errorMessage">{error}</span>}
                      </div>
                    )}
                    <div className="flex flex-col gap-6 md:gap-4">
                      <div className="flex justify-center flex-wrap gap-3">
                        <button
                          type="button"
                          title=" Click to send a new verification pin"
                          disabled={resendDisabled}
                          onClick={() =>
                            handleResendOtp({
                              customerId: customerId,
                              type: "Forgot Password",
                            })
                          }
                          className={`text-sm w-fit ${
                            resendDisabled
                              ? "text-orange cursor-not-allowed"
                              : "text-orange hover:text-secondary cursor-pointer"
                          }`}
                        >
                          {resendDisabled
                            ? `Resend OTP in ${timer}s`
                            : "Resend OTP"}
                        </button>
                      </div>

                      <div className="flex justify-between flex-wrap gap-3">
                        <GeneralButton
                          text="Prev Step"
                          title="Click to change email address"
                          onClick={() => setStep(1)}
                          type="button"
                          bgColor="#ccc"
                          textColor="#000"
                          iconPosition="Left"
                          icon={<TiChevronLeft size={24} className="me-4" />}
                          className="text-24pt"
                        />
                        <GeneralButton
                          text="Verify OTP"
                          title="Click to verify the pin received"
                          type="submit"
                          textColor="#fff"
                          className="text-24pt"
                        />
                      </div>
                    </div>
                  </form>
                )}
              </Formik>
            )}

            {step === 3 && (
              <Formik
                initialValues={{
                  token: otpToken,
                  newPassword: "",
                  confirmPassword: "",
                }}
                validationSchema={newPasswordSchema}
                onSubmit={handlePasswordSubmit}
              >
                {(form) => (
                  <form
                    onSubmit={form.handleSubmit}
                    className="grid grid-cols-1 gap-8"
                  >
                    <CMSFormInput
                      name="newPassword"
                      className="1"
                      label="New Password"
                      type="password"
                      form={form}
                      required
                    />
                    <CMSFormInput
                      name="confirmPassword"
                      label="Confirm Password"
                      className="1"
                      type="password"
                      form={form}
                      required
                    />
                    {error && (
                      <div
                        className={`relative transition-all duration-300 ease-in-out ${
                          error
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 -translate-y-2"
                        }`}
                      >
                        {error && <span className="errorMessage">{error}</span>}
                      </div>
                    )}
                    <div className="flex justify-end">
                      <GeneralButton
                        text="Submit"
                        title="Click to change password"
                        type="submit"
                        className="text-24pt px-4"
                      />
                    </div>
                  </form>
                )}
              </Formik>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ForgotPassword;
